// src/modals/ResaMessageComposerWrapper.jsx — PR #170 (ADR-107 Option Y)
//
// Wrapper léger autour de InboxComposer pour la modal résa (ResaV2Modal).
// Pattern container/presentational : on REUTILISE InboxComposer as-is.
// Pas de duplication de la logique d'envoi (textarea + threading email + POST).
//
// Responsabilités du wrapper (BUG 38 — la sélection des canaux est désormais
// déléguée à InboxComposer, cases à cocher) :
//   1. Disponibilité des canaux selon `item.shape` (gating empty state).
//   2. Calcul du threadId ancre STRICT aligné webhooks (H-1) :
//      - résa liée Beds24    → `beds24:${beds24_booking_id}` (cf. lib/inbox-pull.js L125)
//        PAS UUID résa — c'est l'id booking Beds24 qui identifie le thread OTA.
//      - sinon (email)       → `resend:${itemKind}:${id}` (cf. lib/resend-webhook.js L125)
//   3. Empty state si aucun canal disponible : "Renseignez guest_email…".
//
// Props :
//   - item       : { kind, shape } cohérent ResaV2Modal v1.1 (ADR-105)
//   - itemKind   : 'reservation' | 'pricing_block' (redondant avec item.kind, conservé pour clarté API)
//   - lastInbound: dernier inbox_messages direction=inbound (pour threading email + In-Reply-To)
//   - onSent     : callback({results, canaux, body}) pour optimistic-insert côté caller (H-2)

import { useMemo } from "react"
import { AlertTriangle } from "lucide-react"
import { DS } from "../lib/ui"
import InboxComposer, { isPlatformSource } from "../tabs/messages/InboxComposer"

export default function ResaMessageComposerWrapper({ item, itemKind, lastInbound, onSent }) {
  const shape = item?.shape || {}
  const guestEmail = shape.guest_email
  const beds24BookingId = shape.beds24_booking_id
  const guestPhone = shape.guest_phone
  // Gating plateforme (fix/inbox-canaux-plateformes, QA M2) : résa airbnb/
  // booking (variants V1 inclus) → seul le Chat OTA est joignable, l'alias
  // email/téléphone OTA ne compte pas. Cohérent avec InboxThread.canReply.
  const isPlatform = isPlatformSource(shape.source)

  // BUG 38 + WAGON N — la sélection des canaux est gérée par InboxComposer
  // (cases à cocher, présélection par source, SMS réel, WhatsApp grisé). Le
  // wrapper ne calcule que la disponibilité (gating empty state) + le thread_id.
  // SMS compte comme canal disponible dès qu'un guest_phone existe (ex. résa
  // directe sans email) → évite un empty-state injustifié.
  const availableCanals = useMemo(() => {
    const list = []
    if (isPlatform) {
      // Plateforme : Chat OTA seul (sans booking id → empty state, pas de
      // composer dont l'unique case serait grisée).
      if (beds24BookingId) list.push('tchat_ota')
      return list
    }
    if (guestEmail) list.push('email')
    if (beds24BookingId) list.push('tchat_ota')
    if (guestPhone) list.push('sms')
    return list
  }, [isPlatform, guestEmail, beds24BookingId, guestPhone])

  // H-1 — thread_id ancre STRICT aligné webhooks. On privilégie l'ancre OTA si
  // la résa est liée Beds24 (le thread OTA est identifié par le booking id),
  // sinon l'ancre email (resend:kind:id). Le composer envoie sur les canaux
  // cochés ; ce thread_id sert d'identifiant de fil pour l'audit inbox_messages.
  const threadId = useMemo(() => {
    if (beds24BookingId) {
      // beds24:${beds24_booking_id} (cf. lib/inbox-pull.js L125)
      return `beds24:${beds24BookingId}`
    }
    // resend:${itemKind}:${id} — colon, pas dash (cf. lib/resend-webhook.js L125)
    return `resend:${itemKind || 'reservation'}:${item?.id || shape.id}`
  }, [itemKind, item?.id, shape.id, beds24BookingId])

  // Empty state si aucun canal disponible. Message adapté à la cause :
  // plateforme sans conversation OTA liée ≠ résa directe sans email.
  if (availableCanals.length === 0) {
    return (
      <div style={{padding:"14px 16px",borderTop:`1px solid ${DS.border}`,background:DS.warningBg,fontSize:12,color:DS.warningText,textAlign:"center",lineHeight:1.5}}>
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}><AlertTriangle size={14} strokeWidth={2.2}/> Aucun canal disponible pour écrire au voyageur.</span>
        <br/>
        {isPlatform ? (
          <>Résa plateforme sans conversation OTA liée (<strong>beds24_booking_id</strong> absent) — l'email OTA est un alias inutilisable.</>
        ) : (
          <>Renseignez <strong>guest_email</strong> dans Détails.</>
        )}
      </div>
    )
  }

  return (
    <div style={{borderTop:`1px solid ${DS.border}`,background:DS.pageBg}}>
      {/* InboxComposer réutilisé as-is (Option Y) — il gère désormais lui-même
        * la sélection multi-canal (cases à cocher, WhatsApp grisé).
        * Pour pricing_block : on passe `reservation` SANS `id` (sinon
        * InboxComposer route `reservationId` vers `pb-uuid` et déclenche FK
        * violation sur reservations). pricingBlockId est passé en prop dédiée. */}
      <InboxComposer
        threadId={threadId}
        reservation={itemKind === 'pricing_block' ? { ...shape, id: null } : shape}
        canReply={true}
        lastInbound={lastInbound}
        onSent={onSent}
        pricingBlockId={itemKind === 'pricing_block' ? (item?.id || shape.id) : null}
        source="resa_v2_modal"
      />
    </div>
  )
}
